import React, { useState } from 'react';
import { useStore } from '../../stores/useStore';
import { InventoryItem, InventoryCategory } from '../../types';
import { EmptyState } from '../Common/EmptyState';

const CATEGORIES: InventoryCategory[] = ['냉매', '필터/소모품', '공구', '기타'];

const CATEGORY_UNITS: Record<InventoryCategory, string[]> = {
    '냉매': ['kg', 'L', '캔'],
    '필터/소모품': ['개', '장', '묶음', 'set'],
    '공구': ['개', '세트'],
    '기타': ['개', '장', 'kg', 'L'],
};

const CATEGORY_COLOR: Record<InventoryCategory, { bg: string; text: string }> = {
    '냉매': { bg: '#e3f2fd', text: '#1e3a5f' },
    '필터/소모품': { bg: '#e8f5e9', text: '#5a7a6a' },
    '공구': { bg: '#f3e5f5', text: '#5a5a5a' },
    '기타': { bg: '#f5f5f5', text: '#5a5a5a' },
};

const labelStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 700,
    color: '#5a5a5a',
    marginBottom: '4px',
    display: 'block',
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'white',
    border: '1px solid #c4b8a5',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    fontFamily: "'Noto Sans KR', sans-serif",
    boxSizing: 'border-box',
    color: '#2c2c2c',
};

const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%235a5a5a' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '16px',
    paddingRight: '28px',
};

interface AddForm {
    name: string;
    category: InventoryCategory;
    quantity: string;
    unit: string;
    minQuantity: string;
    notes: string;
}

const defaultForm = (): AddForm => ({
    name: '',
    category: '냉매',
    quantity: '0',
    unit: 'kg',
    minQuantity: '0',
    notes: '',
});

export const InventoryPage: React.FC = () => {
    const { inventoryItems, addInventoryItem, deleteInventoryItem, adjustInventoryQuantity } = useStore();
    const [activeCategory, setActiveCategory] = useState<InventoryCategory | '전체'>('전체');
    const [showAddModal, setShowAddModal] = useState(false);
    const [form, setForm] = useState<AddForm>(defaultForm());

    const filtered = activeCategory === '전체'
        ? inventoryItems
        : inventoryItems.filter((item) => item.category === activeCategory);

    const handleCategoryChange = (cat: InventoryCategory) => {
        setForm((f) => ({ ...f, category: cat, unit: CATEGORY_UNITS[cat][0] }));
    };

    const handleSubmit = () => {
        if (!form.name.trim()) return;
        const newItem: InventoryItem = {
            id: `inv_${Date.now()}`,
            name: form.name.trim(),
            category: form.category,
            quantity: Math.max(0, Number(form.quantity) || 0),
            unit: form.unit,
            minQuantity: Math.max(0, Number(form.minQuantity) || 0),
            notes: form.notes.trim(),
        };
        addInventoryItem(newItem);
        setForm(defaultForm());
        setShowAddModal(false);
    };

    const lowStockCount = inventoryItems.filter(
        (item) => item.minQuantity > 0 && item.quantity <= item.minQuantity
    ).length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* 카테고리 탭 */}
            <div style={{
                display: 'flex',
                backgroundColor: '#f4f1ea',
                borderBottom: '2px solid #c4b8a5',
                overflowX: 'auto',
                flexShrink: 0,
            }}>
                {(['전체', ...CATEGORIES] as (InventoryCategory | '전체')[]).map((cat) => (
                    <button
                        key={cat}
                        onClick={() => setActiveCategory(cat)}
                        style={{
                            padding: '11px 14px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            fontSize: '13px',
                            fontWeight: activeCategory === cat ? 700 : 400,
                            color: activeCategory === cat ? '#1e3a5f' : '#5a5a5a',
                            borderBottom: activeCategory === cat ? '3px solid #c75c2e' : '3px solid transparent',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* 재고 부족 알림 */}
            {lowStockCount > 0 && (
                <div style={{
                    backgroundColor: '#ffebee',
                    border: 'none',
                    borderBottom: '1px solid #c75c2e',
                    padding: '10px 16px',
                    fontSize: '13px',
                    color: '#8b4513',
                    fontWeight: 700,
                    flexShrink: 0,
                }}>
                    ⚠️ 재고 부족 품목 {lowStockCount}개
                </div>
            )}

            {/* 목록 */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#a09890', letterSpacing: '0.5px' }}>
                        {filtered.length}개
                    </span>
                    <button
                        onClick={() => { setForm(defaultForm()); setShowAddModal(true); }}
                        style={{ padding: '6px 12px', backgroundColor: '#1e3a5f', color: 'white', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                    >
                        + 추가
                    </button>
                </div>
                {filtered.length === 0 ? (
                    <EmptyState
                        message="재고 항목이 없습니다"
                        subMessage="+ 추가 버튼으로 등록해보세요"
                    />
                ) : (
                    filtered.map((item) => {
                        const isLow = item.minQuantity > 0 && item.quantity <= item.minQuantity;
                        const catColor = CATEGORY_COLOR[item.category];
                        return (
                            <div
                                key={item.id}
                                style={{
                                    backgroundColor: 'white',
                                    border: `1px solid ${isLow ? '#c75c2e' : '#c4b8a5'}`,
                                    borderRadius: '12px',
                                    padding: '12px 14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                }}
                            >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                        <span style={{
                                            fontSize: '10px',
                                            padding: '2px 6px',
                                            backgroundColor: catColor.bg,
                                            color: catColor.text,
                                            fontWeight: 700,
                                            borderRadius: '10px',
                                            flexShrink: 0,
                                        }}>
                                            {item.category}
                                        </span>
                                        {isLow && (
                                            <span style={{ fontSize: '10px', color: '#c75c2e', fontWeight: 700 }}>
                                                부족
                                            </span>
                                        )}
                                    </div>
                                    <p style={{ fontWeight: 700, color: '#2c2c2c', fontSize: '14px' }}>{item.name}</p>
                                    {item.notes && (
                                        <p style={{ fontSize: '12px', color: '#a09890', marginTop: '2px' }}>{item.notes}</p>
                                    )}
                                    {item.minQuantity > 0 && (
                                        <p style={{ fontSize: '11px', color: '#a09890', marginTop: '2px' }}>
                                            최소 {item.minQuantity}{item.unit}
                                        </p>
                                    )}
                                </div>

                                {/* 수량 조절 */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                    <button
                                        onClick={() => adjustInventoryQuantity(item.id, -1)}
                                        style={{
                                            width: '30px', height: '30px',
                                            border: '1px solid #c4b8a5',
                                            backgroundColor: 'white',
                                            fontSize: '16px',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            borderRadius: '10px',
                                            color: '#5a5a5a',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}
                                    >
                                        −
                                    </button>
                                    <div style={{ textAlign: 'center', minWidth: '52px' }}>
                                        <span style={{
                                            fontSize: '16px',
                                            fontWeight: 700,
                                            color: isLow ? '#c75c2e' : '#2c2c2c',
                                            fontFamily: "'Roboto Mono', monospace",
                                        }}>
                                            {item.quantity}
                                        </span>
                                        <span style={{ fontSize: '12px', color: '#a09890', marginLeft: '3px' }}>
                                            {item.unit}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => adjustInventoryQuantity(item.id, 1)}
                                        style={{
                                            width: '30px', height: '30px',
                                            border: '1px solid #c4b8a5',
                                            backgroundColor: '#1e3a5f',
                                            color: 'white',
                                            fontSize: '16px',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            borderRadius: '10px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}
                                    >
                                        +
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (window.confirm(`'${item.name}'을(를) 삭제하시겠습니까?`)) {
                                                deleteInventoryItem(item.id);
                                            }
                                        }}
                                        style={{
                                            width: '30px', height: '30px',
                                            border: '1px solid #c4b8a5',
                                            backgroundColor: 'white',
                                            color: '#a09890',
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            borderRadius: '10px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}
                                    >
                                        ✕
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>


            {/* 재고 추가 모달 */}
            {showAddModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'flex-end',
                    }}
                    onClick={() => setShowAddModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#f4f1ea',
                            borderTop: '2px solid #c75c2e',
                            borderRadius: '20px 20px 0 0',
                            width: '100%',
                            maxHeight: '85vh',
                            overflowY: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            backgroundColor: '#1e3a5f',
                            color: 'white',
                            padding: '14px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                            <span style={{ fontWeight: 700, fontSize: '15px' }}>재고 추가</span>
                            <button
                                onClick={() => setShowAddModal(false)}
                                style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div>
                                <label style={labelStyle}>품목명 *</label>
                                <input
                                    value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder="예: R410A 냉매"
                                    style={inputStyle}
                                />
                            </div>

                            <div>
                                <label style={labelStyle}>카테고리</label>
                                <select
                                    value={form.category}
                                    onChange={(e) => handleCategoryChange(e.target.value as InventoryCategory)}
                                    style={selectStyle}
                                >
                                    {CATEGORIES.map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={labelStyle}>현재 수량</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={form.quantity}
                                        onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                                        style={inputStyle}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={labelStyle}>단위</label>
                                    <select
                                        value={form.unit}
                                        onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                                        style={selectStyle}
                                    >
                                        {CATEGORY_UNITS[form.category].map((u) => (
                                            <option key={u} value={u}>{u}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label style={labelStyle}>최소 재고 (부족 경고 기준)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={form.minQuantity}
                                    onChange={(e) => setForm((f) => ({ ...f, minQuantity: e.target.value }))}
                                    placeholder="0 = 경고 없음"
                                    style={inputStyle}
                                />
                            </div>

                            <div>
                                <label style={labelStyle}>메모</label>
                                <input
                                    value={form.notes}
                                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                                    placeholder="예: 창고 2번 선반"
                                    style={inputStyle}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '8px', paddingTop: '4px', paddingBottom: '8px' }}>
                                <button
                                    onClick={() => setShowAddModal(false)}
                                    style={{
                                        flex: 1,
                                        padding: '13px',
                                        border: '1px solid #c4b8a5',
                                        backgroundColor: 'white',
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        borderRadius: '10px',
                                        color: '#5a5a5a',
                                    }}
                                >
                                    취소
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={!form.name.trim()}
                                    style={{
                                        flex: 2,
                                        padding: '13px',
                                        border: 'none',
                                        backgroundColor: form.name.trim() ? '#c75c2e' : '#c4b8a5',
                                        color: 'white',
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        cursor: form.name.trim() ? 'pointer' : 'not-allowed',
                                        borderRadius: '10px',
                                    }}
                                >
                                    추가
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
